import { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import LiveOps from './pages/LiveOps';
import Cosmetics from './pages/Cosmetics';
import Operations from './pages/Operations';
import Bundles from './pages/Bundles';
import EventPreview from './pages/EventPreview';
import CosmeticPreview from './pages/CosmeticPreview';
import MascotDialogs from './pages/MascotDialogs';
import Users from './pages/Users';
import { getAdminToken, fetchAdminTokenFromAccess } from './lib/api';

/** Guard that redirects to /login if no admin token is set. */
function AuthGuard({ children }: { children: React.ReactNode }) {
  const token = getAdminToken();
  if (!token) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

export default function App() {
  // On first load, try to fetch the admin token from the Access Pages Function.
  // If Access is not configured (local dev) or the user isn't authenticated,
  // this silently fails and the user sees the Login page as before.
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (getAdminToken()) {
      setChecking(false);
      return;
    }
    fetchAdminTokenFromAccess().finally(() => setChecking(false));
  }, []);

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-950">
        <div className="text-gray-400">Loading…</div>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        path="/"
        element={
          <AuthGuard>
            <Layout />
          </AuthGuard>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="liveops" element={<LiveOps />} />
        <Route path="liveops/:id/preview" element={<EventPreview />} />
        <Route path="cosmetics" element={<Cosmetics />} />
        <Route path="cosmetics/:id/preview" element={<CosmeticPreview />} />
        <Route path="bundles" element={<Bundles />} />
        <Route path="mascot-dialogs" element={<MascotDialogs />} />
        <Route path="users" element={<Users />} />
        <Route path="operations" element={<Operations />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
